import * as z from "zod/v4";
import { v4 as uuid } from "uuid";
import {
  batch,
  computed,
  effect,
  signal,
  type ReadonlySignal,
  type Signal,
} from "@preact/signals";
import type { CasualOSManager } from "./OsManager";
import type { LoginManager } from "./LoginManager";
import { isMinimalEmbedUrl } from "./EmbedMode";
import type { NavigationManager } from "./NavigationManager";
import type { CustomizationVariantSelectionsManager } from "./CustomizationVariantSelectionsManager";
import type { CustomizationExtensionPreferencesManager } from "./CustomizationExtensionPreferencesManager";
import type { ExtensionSettingValue } from "./ExtensionManager";
import {
  sensitiveProxyPointerSchema,
  type SensitiveProxyPointer,
} from "./ExtensionSensitiveSettings";
import {
  applyHighlightOverrides,
  DARK_THEME,
  filterValidColorOverrides,
  filterValidFontFamilyOverrides,
  LIGHT_THEME,
  LIGHT_THEME_FONT_DEFAULTS,
  SYSTEM_THEME_ID,
  type BibleTheme,
  type HighlightOverrides,
  type ThemeColorKey,
  type ThemeFontFamilyKey,
  type ThemeHighlightColor,
  type ThemeManager,
  type ThemeOverrides,
} from "./ThemeManager";

export const CUSTOMIZATION_MARKER = "publicRead:seedBibleCustomization";

function hexToRgb(hex: string): [number, number, number] {
  const clean = hex.replace("#", "");
  const full =
    clean.length === 3
      ? clean
          .split("")
          .map((c) => c + c)
          .join("")
      : clean;
  const num = parseInt(full, 16);
  return [(num >> 16) & 255, (num >> 8) & 255, num & 255];
}

function rgbToHex([r, g, b]: [number, number, number]): string {
  return (
    "#" +
    [r, g, b]
      .map((c) =>
        Math.round(Math.min(255, Math.max(0, c)))
          .toString(16)
          .padStart(2, "0")
      )
      .join("")
  );
}

function rgbToHsl([r, g, b]: [number, number, number]): [
  number,
  number,
  number,
] {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  const d = max - min;
  if (d === 0) {
    return [0, 0, l];
  }
  const s = d / (1 - Math.abs(2 * l - 1));
  let h: number;
  if (max === rn) {
    h = ((gn - bn) / d) % 6;
  } else if (max === gn) {
    h = (bn - rn) / d + 2;
  } else {
    h = (rn - gn) / d + 4;
  }
  h *= 60;
  if (h < 0) {
    h += 360;
  }
  return [h, s, l];
}

function hslToRgb([h, s, l]: [number, number, number]): [
  number,
  number,
  number,
] {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  const [r1, g1, b1] =
    h < 60
      ? [c, x, 0]
      : h < 120
        ? [x, c, 0]
        : h < 180
          ? [0, c, x]
          : h < 240
            ? [0, x, c]
            : h < 300
              ? [x, 0, c]
              : [c, 0, x];
  return [(r1 + m) * 255, (g1 + m) * 255, (b1 + m) * 255];
}

/** Lightens a hex color toward white by `amount` (0-1) of its remaining headroom. */
export function lightenColor(hex: string, amount: number): string {
  const [h, s, l] = rgbToHsl(hexToRgb(hex));
  return rgbToHex(hslToRgb([h, s, l + (1 - l) * amount]));
}

export const SECONDARY_LIGHTEN_AMOUNT = 0.35;
export const TERTIARY_LIGHTEN_AMOUNT = 0.55;

/** Linearizes one sRGB channel (0-255) per the WCAG relative-luminance formula. */
function srgbChannelToLinear(channel: number): number {
  const c = channel / 255;
  return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

/** WCAG relative luminance (0-1) of a hex color. */
function relativeLuminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex);
  return (
    0.2126 * srgbChannelToLinear(r) +
    0.7152 * srgbChannelToLinear(g) +
    0.0722 * srgbChannelToLinear(b)
  );
}

/**
 * WCAG contrast ratio between two colors, from 1 (identical) to 21 (black on
 * white) — https://www.w3.org/TR/WCAG21/#contrast-minimum. Symmetric: the
 * argument order doesn't matter.
 */
export function getContrastRatio(hexA: string, hexB: string): number {
  const luminanceA = relativeLuminance(hexA);
  const luminanceB = relativeLuminance(hexB);
  const lighter = Math.max(luminanceA, luminanceB);
  const darker = Math.min(luminanceA, luminanceB);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * WCAG AA's minimum contrast ratio for normal-sized text. Below this, text
 * is considered hard to read against its background.
 */
export const MIN_READABLE_CONTRAST_RATIO = 4.5;

// Darkest-last reds for light backgrounds, then lightest-last for dark ones,
// so the first readable pick is the most saturated red that still reads.
const WORDS_OF_JESUS_RED_CANDIDATES = [
  "#c00",
  "#b00",
  "#a00",
  "#900",
  "#800",
  "#700",
  "#ff8080",
  "#ffa0a0",
  "#ffc0c0",
  "#ffe0e0",
];

/**
 * A red for words of Jesus that stays readable on `background` (a hex color),
 * or `undefined` if `background` isn't a hex color. When no red reaches
 * {@link MIN_READABLE_CONTRAST_RATIO} (a mid-tone background), returns the
 * highest-contrast one.
 */
export function getReadableWordsOfJesusColor(
  background: string
): string | undefined {
  if (!/^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.test(background)) {
    return undefined;
  }
  const readable = WORDS_OF_JESUS_RED_CANDIDATES.find(
    (red) => getContrastRatio(red, background) >= MIN_READABLE_CONTRAST_RATIO
  );
  if (readable) {
    return readable;
  }
  return WORDS_OF_JESUS_RED_CANDIDATES.reduce((best, red) =>
    getContrastRatio(red, background) > getContrastRatio(best, background)
      ? red
      : best
  );
}

export interface CustomizationContrastPair {
  /** The text/foreground color field. */
  foreground: ThemeColorKey;
  /** The field it's expected to be read against. */
  background: ThemeColorKey;
  /** Human-readable description of the pair, e.g. for a warning tooltip. */
  label: string;
}

/**
 * Every foreground/background color pair in a theme that's meant to hold
 * readable text — e.g. `primaryFontColor` text drawn on a `primaryColor`
 * surface. Used to warn when a customization's colors don't leave enough
 * contrast to stay readable. Deliberately excludes fields with no natural
 * reading pair (`tertiaryColor`, `dividerColor`,
 * `selectedVerseTextDecorationColor`) — those aren't backgrounds text sits
 * on top of.
 */
export const CUSTOMIZATION_CONTRAST_PAIRS: CustomizationContrastPair[] = [
  {
    foreground: "primaryFontColor",
    background: "primaryColor",
    label: "Primary text on primary",
  },
  {
    foreground: "secondaryFontColor",
    background: "secondaryColor",
    label: "Secondary text on secondary",
  },
  {
    foreground: "fontColor",
    background: "background",
    label: "Text on app background",
  },
  {
    foreground: "linkColor",
    background: "readerBackground",
    label: "Link on reader background",
  },
  {
    foreground: "linkVisitedColor",
    background: "readerBackground",
    label: "Visited link on reader background",
  },
  {
    foreground: "readerFontColor",
    background: "readerBackground",
    label: "Reader text on reader background",
  },
  {
    foreground: "bookTitleFontColor",
    background: "readerBackground",
    label: "Book title on reader background",
  },
  {
    foreground: "chapterHeadingFontColor",
    background: "readerBackground",
    label: "Chapter heading on reader background",
  },
  {
    foreground: "verseFontColor",
    background: "readerBackground",
    label: "Verse text on reader background",
  },
  {
    foreground: "hebrewSubtitleFontColor",
    background: "readerBackground",
    label: "Hebrew subtitle on reader background",
  },
  {
    foreground: "sidebarFontColor",
    background: "sidebarBackground",
    label: "Sidebar text on sidebar background",
  },
  {
    foreground: "readerToolbarFontColor",
    background: "readerToolbarBackground",
    label: "Reader toolbar text on reader toolbar background",
  },
  {
    foreground: "readerToolbarFloatingButtonFontColor",
    background: "readerToolbarFloatingButtonBackground",
    label: "Floating button text on floating button background",
  },
];

const customizationVariantHighlightColorSchema = z.object({
  color: z.string().optional(),
  fontColor: z.string().optional(),
  wordsOfJesusFontColor: z.string().optional(),
});

const customizationVariantSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  /**
   * Id of the built-in preset (an entry in `theme.themes.value`, e.g.
   * "light"/"dark") this theme falls back to for any field not present in
   * `themes`/`highlightColors` below. Defaults to "light" for records
   * persisted before this field existed — harmless, since those records'
   * `themes`/`highlightColors` were always fully populated at creation
   * time, so there's nothing left for a base theme to fill in.
   */
  baseTheme: z.string().min(1).default("light"),
  /**
   * Color/font overrides explicitly set by the user, keyed by
   * `ThemeColorKey`/`ThemeFontFamilyKey`. A key's absence means "inherit
   * `baseTheme`'s value", not "no value" — see `buildBibleThemeFromCustomizationTheme`.
   */
  themes: z.record(z.string(), z.string()),
  /**
   * Per-highlight-id color overrides, in the same shape as `ThemeManager`'s
   * own `HighlightOverrides` — keyed by highlight id (e.g. "yellow"), each
   * entry a partial `{ color, fontColor, wordsOfJesusFontColor }` patch. A
   * highlight id (or one of its fields) absent here also means "inherit
   * from `baseTheme`."
   */
  highlightColors: z
    .record(z.string(), customizationVariantHighlightColorSchema)
    .default({}),
  createdAt: z.number(),
  updatedAt: z.number(),
});

/**
 * How a customization treats one extension from the app's known catalog
 * while it's active:
 * - `available`: shown in Settings → Extensions; the viewer may install/uninstall it
 *   themselves, same as outside any customization.
 * - `auto-installed`: force-installed with no prompt while the
 *   customization is active; shown but can't be uninstalled from there.
 * - `hidden`: never offered in Settings → Extensions while the
 *   customization is active. If it's still installed anyway (e.g. as another
 *   extension's dependency) it's listed under Installed and can still be uninstalled.
 */
export const EXTENSION_AVAILABILITY_VALUES = [
  "available",
  "auto-installed",
  "hidden",
] as const;
export type ExtensionAvailability =
  (typeof EXTENSION_AVAILABILITY_VALUES)[number];
const extensionAvailabilitySchema = z.enum(EXTENSION_AVAILABILITY_VALUES);

/**
 * The availabilities a customization may apply to every extension it has no
 * explicit entry for. `auto-installed` is deliberately excluded: it installs
 * with no prompt, so it must only ever follow an author's review of that one
 * extension, never reach one added to the catalog after they last looked.
 */
export const DEFAULT_EXTENSION_AVAILABILITY_VALUES = [
  "available",
  "hidden",
] as const;
export type DefaultExtensionAvailability =
  (typeof DEFAULT_EXTENSION_AVAILABILITY_VALUES)[number];

const customizationSchema = z
  .object({
    id: z.string().min(1),
    name: z.string().min(1),
    variants: z.array(customizationVariantSchema).min(1),
    defaultVariantId: z.string().min(1),
    logoUrl: z.url().max(1024).optional().nullable(),
    /**
     * The translation viewers of this customization should start reading in,
     * overriding Seed Bible's own per-language default (see
     * `DEFAULT_TRANSLATIONS_BY_LANGUAGE` in `BibleReadingManager`) the same
     * way `BrandingConfig.defaultTranslationId` does for a whole deployment.
     * Absent means "no override — use Seed Bible's normal default."
     */
    defaultTranslationId: z.string().min(1).optional(),
    createdAt: z.number(),
    updatedAt: z.number(),
    /**
     * Per-extension availability while this customization is active,
     * keyed by extension id. Every id must reference the app's own known
     * extension catalog — never a URL or other free-form identity, since
     * `auto-installed` installs with no confirmation step. An id with no
     * entry here follows `defaultExtensionAvailability`. Only ever written
     * from the select in CustomizationEditExtensionsView.
     */
    extensionSettings: z
      .record(z.string(), extensionAvailabilitySchema)
      .default({}),
    /**
     * The availability of every extension with no entry in
     * `extensionSettings`, including ones added to the catalog later. Absent
     * (records saved before this existed) means `available`.
     */
    defaultExtensionAvailability: z
      .enum(DEFAULT_EXTENSION_AVAILABILITY_VALUES)
      .optional(),
    /**
     * Default values this customization sets for extension settings
     * (`ExtensionMeta.settings`), keyed by extension id then setting name.
     * A viewer's own value (see `ExtensionSettingsManager`) always wins over
     * this; this only fills in for a viewer who hasn't set one themselves.
     * Only ever written from the extension settings editor in
     * `CustomizationEditExtensionsView`.
     */
    extensionSettingDefaults: z
      .record(
        z.string(),
        z.record(z.string(), z.union([z.string(), z.boolean(), z.number()]))
      )
      .default({}),
    /**
     * Where this customization's own values for extension sensitive settings
     * are held, keyed by extension id then `sensitive` entry id. Only the
     * addresses: the values live in proxy records in the owner's record, and
     * a viewer's own values (see `ExtensionSensitiveSettings`) win over them.
     * Only ever written by `setExtensionSensitiveProxy`.
     */
    extensionSensitiveProxies: z
      .record(z.string(), z.record(z.string(), sensitiveProxyPointerSchema))
      .default({}),
  })
  .refine((r) => r.variants.some((v) => v.id === r.defaultVariantId), {
    message: "defaultVariantId must reference an existing variant",
  });

export interface CustomizationThemeVariant {
  id: string;
  name: string;
  /**
   * Id of the built-in preset (an entry in `theme.themes.value`) this theme
   * falls back to for any field not present in `themes`/`highlightColors`.
   * See `buildBibleThemeFromCustomizationTheme`.
   */
  baseTheme: string;
  /**
   * Color/font overrides explicitly set by the user, in the same shape as
   * `ThemeManager`'s own `ThemeOverrides`. A key's absence means "inherit
   * `baseTheme`'s value" — this is what makes "has the user overridden
   * this field" a simple presence check rather than something tracked
   * separately.
   */
  themes: ThemeOverrides;
  /** Per-highlight-id color overrides, in the same shape as `ThemeManager`'s own `HighlightOverrides`. Absence follows the same inherit-from-`baseTheme` rule as `themes`. */
  highlightColors: HighlightOverrides;
  createdAt: number;
  updatedAt: number;
}

export interface SeedBibleCustomization {
  id: string;
  name: string;
  /** Named theme variants (e.g. a "Light" and "Dark" pair) authored for this customization. Always has at least 1 entry. */
  variants: CustomizationThemeVariant[];
  /** The variant id shown to a viewer who hasn't picked one for this customization yet. Always references an entry in `variants`. */
  defaultVariantId: string;
  logoUrl?: string | null;
  /**
   * The translation viewers should start reading in while this
   * customization is active, overriding Seed Bible's per-language default.
   * Absent means no override.
   */
  defaultTranslationId?: string;
  createdAt: number;
  updatedAt: number;
  /** Per-extension availability while this customization is active. An id with no entry follows `defaultExtensionAvailability`. */
  extensionSettings: Record<string, ExtensionAvailability>;
  /** Availability of every extension with no entry in `extensionSettings`. Absent means "available". */
  defaultExtensionAvailability?: DefaultExtensionAvailability;
  /** Per-extension setting defaults while this customization is active. An extension/key with no entry has no default here. */
  extensionSettingDefaults: Record<
    string,
    Record<string, ExtensionSettingValue>
  >;
  /** Per-extension sensitive proxies this customization provides. An extension/entry with none here provides nothing. */
  extensionSensitiveProxies: Record<
    string,
    Record<string, SensitiveProxyPointer>
  >;
}

function withExtensionSensitiveProxy(
  customization: SeedBibleCustomization,
  extensionId: string,
  proxyId: string,
  pointer: SensitiveProxyPointer | null,
  updatedAt: number
): SeedBibleCustomization {
  const byProxy = { ...customization.extensionSensitiveProxies[extensionId] };
  if (pointer) {
    byProxy[proxyId] = pointer;
  } else {
    delete byProxy[proxyId];
  }
  const next = { ...customization.extensionSensitiveProxies };
  if (Object.keys(byProxy).length === 0) {
    delete next[extensionId];
  } else {
    next[extensionId] = byProxy;
  }
  return {
    ...customization,
    extensionSensitiveProxies: next,
    updatedAt,
  };
}

/** The availability a customization gives extensions it has no explicit entry for. "available" when none is given. */
export function getDefaultExtensionAvailability(
  customization: SeedBibleCustomization | null
): DefaultExtensionAvailability {
  return customization?.defaultExtensionAvailability ?? "available";
}

/** Resolves an extension's effective availability for a customization, falling back to its default when the id has no explicit entry. */
export function getExtensionAvailability(
  customization: SeedBibleCustomization | null,
  extensionId: string
): ExtensionAvailability {
  return (
    customization?.extensionSettings[extensionId] ??
    getDefaultExtensionAvailability(customization)
  );
}

/** Resolves a customization's default for one extension setting, or undefined if it doesn't set one. */
export function getExtensionSettingDefault(
  customization: SeedBibleCustomization | null,
  extensionId: string,
  key: string
): ExtensionSettingValue | undefined {
  return customization?.extensionSettingDefaults[extensionId]?.[key];
}

/** PostHog event/person property holding the `recordName.id` locator of the customization the viewer arrived through. */
export const POSTHOG_CUSTOMIZATION_PROPERTY = "customization_id";

function buildCustomizationLocator(recordName: string, id: string): string {
  return `${recordName}.${id}`;
}

function narrowVariants(
  variants: {
    id: string;
    name: string;
    baseTheme: string;
    themes: Record<string, string>;
    highlightColors: HighlightOverrides;
    createdAt: number;
    updatedAt: number;
  }[]
): CustomizationThemeVariant[] {
  return variants.map((variant) => ({
    ...variant,
    themes: {
      ...filterValidColorOverrides(variant.themes),
      ...filterValidFontFamilyOverrides(variant.themes),
    },
  }));
}

export interface CustomizationColorGroup {
  id: string;
  title: string;
  fields: { key: ThemeColorKey; label: string }[];
}

export const CUSTOMIZATION_COLOR_GROUPS: CustomizationColorGroup[] = [
  {
    id: "brand",
    title: "Brand",
    fields: [
      { key: "primaryColor", label: "Primary" },
      { key: "primaryFontColor", label: "Primary text" },
      { key: "secondaryColor", label: "Secondary" },
      { key: "secondaryFontColor", label: "Secondary text" },
      { key: "tertiaryColor", label: "Tertiary" },
      { key: "linkColor", label: "Link" },
      { key: "linkVisitedColor", label: "Visited link" },
    ],
  },
  {
    id: "surfaces",
    title: "Surfaces",
    fields: [
      { key: "background", label: "App background" },
      { key: "readerBackground", label: "Reader background" },
      { key: "sidebarBackground", label: "Sidebar background" },
      { key: "readerToolbarBackground", label: "Reader toolbar background" },
      {
        key: "readerToolbarFloatingButtonBackground",
        label: "Floating button background",
      },
      { key: "dividerColor", label: "Divider" },
    ],
  },
  {
    id: "text",
    title: "Text",
    fields: [
      { key: "fontColor", label: "Text" },
      { key: "readerFontColor", label: "Reader text" },
      { key: "sidebarFontColor", label: "Sidebar text" },
      { key: "bookTitleFontColor", label: "Book title" },
      { key: "chapterHeadingFontColor", label: "Chapter heading" },
      { key: "verseFontColor", label: "Verse" },
      { key: "hebrewSubtitleFontColor", label: "Hebrew subtitle" },
      { key: "readerToolbarFontColor", label: "Reader toolbar text" },
      {
        key: "readerToolbarFloatingButtonFontColor",
        label: "Floating button text",
      },
    ],
  },
  {
    id: "selection",
    title: "Verse selection",
    fields: [
      {
        key: "selectedVerseTextDecorationColor",
        label: "Selected verse decoration",
      },
    ],
  },
];

export const CUSTOMIZATION_COLOR_FIELDS: {
  key: ThemeColorKey;
  label: string;
}[] = CUSTOMIZATION_COLOR_GROUPS.flatMap((group) => group.fields);

export interface CustomizationFontField {
  key: ThemeFontFamilyKey;
  label: string;
}

export const CUSTOMIZATION_FONT_FIELDS: CustomizationFontField[] = [
  { key: "fontFamily", label: "Default" },
  { key: "bookTitleFontFamily", label: "Book title" },
  { key: "chapterHeadingFontFamily", label: "Chapter heading" },
  { key: "verseFontFamily", label: "Verse" },
  { key: "hebrewSubtitleFontFamily", label: "Hebrew subtitle" },
];

export interface CustomizationFontPreset {
  name: string;
  value: string;
}

export const CUSTOMIZATION_FONT_PRESETS: CustomizationFontPreset[] = [
  { name: "Newsreader", value: "Newsreader, serif" },
  { name: "System UI", value: "system-ui, sans-serif" },
  { name: "Roboto", value: "Roboto, sans-serif" },
  { name: "Open Sans", value: "Open Sans, sans-serif" },
  { name: "Playfair Display", value: "Playfair Display, serif" },
  { name: "Cormorant Garamond", value: "Cormorant Garamond, serif" },
  { name: "Plus Jakarta Sans", value: "Plus Jakarta Sans, sans-serif" },
];

/**
 * The presets for one font field, with a "Default" entry (that field's
 * value in the Seed Bible Light theme) prepended. "Default" is
 * field-specific — unlike the 7 named presets, which apply the exact same
 * value regardless of which of the 5 fields is being edited — so it can't
 * live in `CUSTOMIZATION_FONT_PRESETS` itself.
 */
export function getFontPresetsForField(
  key: ThemeFontFamilyKey
): CustomizationFontPreset[] {
  return [
    { name: "Default", value: LIGHT_THEME_FONT_DEFAULTS[key] },
    ...CUSTOMIZATION_FONT_PRESETS,
  ];
}

/**
 * Builds a font-family CSS value for a manually-typed Google Font name.
 * Restricted to letters/digits/spaces (real font names never need more)
 * both so the stored CSS value can't be broken and so it's safe to later
 * reuse as a Google Fonts API query parameter with no further escaping.
 */
export function buildCustomFontValue(name: string): string {
  const trimmed = name
    .replace(/[^A-Za-z0-9 ]/g, "")
    .trim()
    .replace(/\s+/g, " ");
  return trimmed ? `${trimmed}, sans-serif` : "";
}

/**
 * A brand-new variant starts with no overrides at all — every field is
 * inherited from `baseThemeId` until the user explicitly edits it. This is
 * what makes "has the user overridden this field" a simple presence check
 * (see `buildBibleThemeFromCustomizationTheme`) rather than something that
 * has to be tracked separately from the values themselves.
 */
function buildVariant(
  name: string,
  baseThemeId: string
): CustomizationThemeVariant {
  const now = Date.now();
  return {
    id: `variant_${uuid()}`,
    name,
    baseTheme: baseThemeId,
    themes: {},
    highlightColors: {},
    createdAt: now,
    updatedAt: now,
  };
}

/**
 * Resolves a customization theme's full, ready-to-render `BibleTheme` by
 * layering its own explicit overrides (`variant.themes`/`highlightColors`)
 * on top of `basePreset`. A field absent from the variant falls through to
 * `basePreset`'s own value unchanged — this is the one place that
 * inherit-vs-override distinction gets resolved into a concrete value, so
 * `variant.themes`/`highlightColors` should never be read as if either were
 * a complete theme on their own.
 */
export function buildBibleThemeFromCustomizationTheme(
  variant: CustomizationThemeVariant,
  basePreset: BibleTheme
): BibleTheme {
  const withVariables =
    Object.keys(variant.themes).length === 0
      ? basePreset
      : {
          ...basePreset,
          variables: { ...basePreset.variables, ...variant.themes },
        };
  return applyHighlightOverrides(withVariables, variant.highlightColors);
}

export interface CustomizationsManager {
  customizations: Signal<SeedBibleCustomization[]>;
  isLoading: Signal<boolean>;
  /**
   * The customization currently applied to the live theme, if any. There
   * are exactly two ways for one to become active: the in-progress edit
   * draft, if one is open (so unsaved edits preview live), or a
   * `?customization=...` share link. The draft takes priority, so opening
   * an editor always previews that customization even if a different one
   * was loaded via the URL.
   */
  activeCustomization: ReadonlySignal<SeedBibleCustomization | null>;
  /**
   * `recordName.id` for `activeCustomization`, or null when nothing is
   * active (or a draft is open before the owner is signed in, so there is
   * no record name to build a share link from).
   */
  activeCustomizationLocator: ReadonlySignal<string | null>;
  /** The variant of the active customization currently in effect (the device's color scheme when `isFollowingSystemScheme`, else the viewer's own pick, else the customization's default, else its first variant). */
  activeVariant: ReadonlySignal<CustomizationThemeVariant | null>;
  /** Whether the active customization has both a Light-based and a Dark-based variant, and so can follow the device the way the app-wide System theme does. False when nothing is active. */
  canFollowSystemScheme: ReadonlySignal<boolean>;
  /**
   * Whether `activeVariant` is tracking the device right now — the viewer
   * picked the System card (stored as the `SYSTEM_THEME_ID` sentinel in
   * their variant selections), or never picked a variant here and their
   * app-wide theme is System.
   */
  isFollowingSystemScheme: ReadonlySignal<boolean>;
  /**
   * The active variant's colors, session-only: layered on top of the
   * rendered theme by `SeedBibleStateManager`, never written to
   * `SettingsManager` — a refresh always reverts to the user's real theme.
   */
  activeThemeOverrides: ReadonlySignal<ThemeOverrides>;
  /**
   * The active variant's per-highlight-id color overrides, session-only —
   * same lifetime and rendering path as `activeThemeOverrides`, just for
   * highlight colors instead of flat theme variables.
   */
  activeHighlightOverrides: ReadonlySignal<HighlightOverrides>;
  /**
   * The active variant's full, resolved `BibleTheme` — its own overrides
   * combined with its `baseTheme` preset via
   * `buildBibleThemeFromCustomizationTheme`. `null` when nothing is active.
   * This is what `SeedBibleStateManager` actually renders: the active
   * variant's colors are always shown against its own designated base
   * preset (e.g. a "Dark" theme always looks dark), never against whatever
   * the viewer's own personal light/dark theme happens to be.
   */
  activeResolvedTheme: ReadonlySignal<BibleTheme | null>;
  /** Resolves a variant's `baseTheme` id to the actual preset, falling back to the viewer's current preset if the id is unrecognized (e.g. a preset was removed). */
  resolveVariantBaseTheme: (variant: CustomizationThemeVariant) => BibleTheme;
  /**
   * `variant`'s resolved theme (base preset + its own overrides), with any
   * pending `previewEditingVariant*` override layered on top. This is what
   * `activeResolvedTheme` uses, and what `CustomizationEditPane` should use
   * to render a variant it's editing, so a `ColorPicker` drag previews live
   * without touching the draft until the picker commits.
   */
  resolveEditingVariantTheme: (
    variant: CustomizationThemeVariant
  ) => BibleTheme;
  /**
   * The extension ids that should be installed while the active
   * customization is in effect: its own `auto-installed` extensions (minus
   * any the viewer removed), unioned with any `available` extras the viewer
   * added for it via `addExtensionToActiveCustomization`. Empty when
   * nothing is active.
   */
  activeExtensionIds: ReadonlySignal<string[]>;
  /**
   * A customization loaded via the `?customization={recordName}.{id}` share
   * link, if any. Used by `activeCustomization` whenever there's no
   * in-progress edit draft open.
   */
  linkedCustomization: ReadonlySignal<SeedBibleCustomization | null>;
  /**
   * Resolves once the initial `?customization=...` load (if any) reaches a
   * terminal outcome: loaded, failed, or (during SSR only) exceeded a
   * deadline. Resolves immediately if there was no `?customization=` param.
   * Throw this in a component to suspend SSR rendering until then.
   *
   * Never rejects — a rejected promise thrown during `renderToStringAsync`
   * surfaces as a render exception and takes down the whole SSR document.
   */
  initialCustomizationLoadPromise: Promise<void>;
  /** True once the initial `?customization=` load has settled — see above. */
  initialCustomizationLoadSettled: ReadonlySignal<boolean>;
  /**
   * Builds a seed of the initial `?customization=...` load for the client to
   * reuse instead of re-fetching — see `InitialCustomizationSeed`. Called by
   * `entry-ssr.tsx` after rendering settles; null when there was no
   * `?customization=` param, or when the load hadn't actually completed (the
   * SSR-only timeout backstop fired first).
   */
  getInitialCustomizationSeed: () => InitialCustomizationSeed | null;
  /**
   * The local, unpersisted draft of the customization currently open in the
   * editor settings pages, or null when none is open. Edits accumulate here
   * and are only written to CasualOS by `saveEditingCustomization`.
   */
  editingCustomization: Signal<SeedBibleCustomization | null>;
  /** The variant id the variant editor settings page should show. */
  editingVariantId: Signal<string | null>;
  load: () => Promise<void>;
  /** Loads a customization by its `{recordName}.{id}` locator into `linkedCustomization`. */
  loadByLocator: (locator: string) => Promise<void>;
  /** Creates a new customization with one variant based on the viewer's current preset (no overrides of its own yet). */
  create: () => Promise<SeedBibleCustomization>;
  /** Seeds `editingCustomization` from the persisted record with this id. No-ops if not found. */
  startEditing: (id: string) => void;
  /**
   * Clears `editingCustomization` and `editingVariantId` immediately. Any
   * edit still waiting on the auto-save debounce (see
   * `saveEditingCustomization`) is flushed in the background at the same
   * time, so closing the editor right after a change doesn't drop it.
   */
  stopEditing: () => void;
  /**
   * Same as `stopEditing`, except a pending auto-save is cancelled instead
   * of flushed — the draft's unsaved changes are actually thrown away. Used
   * by the "Discard changes" action of the unsaved-changes confirmation
   * shown when closing the editor with `hasUnsavedChanges` true.
   */
  discardEditingCustomization: () => void;
  /**
   * True while `editingCustomization` differs from what's actually
   * persisted (a pending or in-flight auto-save). `false` when there's no
   * open draft.
   */
  hasUnsavedChanges: ReadonlySignal<boolean>;
  /** Persists `editingCustomization` and upserts it into `customizations`. No-op if there's no draft or the user is signed out. Also called automatically, debounced by 5 seconds, whenever one of the draft mutators below changes the draft. */
  saveEditingCustomization: () => Promise<void>;
  /** Rejects, keeping the customization, if it or one of its sensitive proxies can't be erased. */
  remove: (id: string) => Promise<void>;
  /** Uploads the file, stages the resulting URL onto the open draft, and immediately persists it (unlike every other draft field, which auto-saves only after a short debounce). */
  uploadLogo: (file: File) => Promise<void>;
  /** A shareable link that auto-loads this customization via `loadByLocator`. */
  getShareLink: (customization: SeedBibleCustomization) => string;
  // Synchronous, draft-only mutators. Each no-ops if `editingCustomization` is
  // null, and otherwise queues a debounced auto-save of the draft.
  updateEditingName: (name: string) => void;
  /** Sets (or clears, given `null`) the draft's default translation. No-op with no open draft. */
  updateEditingDefaultTranslationId: (translationId: string | null) => void;
  /** Clears the draft's logo and immediately persists it (unlike every other draft field, which auto-saves only after a short debounce). No-op with no open draft. */
  removeEditingLogo: () => Promise<void>;
  /** Adds a new variant to the draft, based on the viewer's current preset (no overrides of its own yet). */
  addEditingVariant: () => CustomizationThemeVariant | null;
  renameEditingVariant: (variantId: string, name: string) => void;
  /**
   * Changes which built-in preset (`presetId`, one of `theme.themes.value`'s
   * own ids) one variant falls back to for anything it hasn't explicitly
   * overridden — e.g. "base this dark theme on Seed Bible's own Dark
   * preset." The variant's own overrides (`themes`/`highlightColors`) are
   * left completely untouched; only fields the user hasn't customized will
   * visually change. No-ops if there's no open draft or the preset id is
   * unrecognized.
   */
  applyPresetToEditingVariant: (variantId: string, presetId: string) => void;
  setEditingVariantColor: (
    variantId: string,
    key: ThemeColorKey,
    value: string
  ) => void;
  setEditingVariantFont: (
    variantId: string,
    key: ThemeFontFamilyKey,
    value: string
  ) => void;
  /** Patches one highlight id's color overrides on the draft's variant. Fields omitted from `patch` are left as they were. */
  setEditingVariantHighlightColor: (
    variantId: string,
    highlightId: string,
    patch: Partial<ThemeHighlightColor>
  ) => void;
  /** Removes one color/font field's override, reverting it to inherit from the variant's `baseTheme`. No-op with no open draft. */
  resetEditingVariantField: (
    variantId: string,
    key: ThemeColorKey | ThemeFontFamilyKey
  ) => void;
  /** Removes all of one highlight id's overrides, reverting it to inherit from the variant's `baseTheme`. No-op with no open draft. */
  resetEditingVariantHighlightColor: (
    variantId: string,
    highlightId: string
  ) => void;
  /**
   * Live, non-committing preview of one color field on a draft variant —
   * reflected in `resolveEditingVariantTheme` immediately, but never
   * written into `editingCustomization` or auto-saved. Meant to be called
   * on a `ColorPicker`'s `onPreview` while the user drags;
   * `setEditingVariantColor` clears any pending preview for the same
   * variant/key once the color is actually committed.
   */
  previewEditingVariantColor: (
    variantId: string,
    key: ThemeColorKey,
    value: string
  ) => void;
  /** Discards a pending `previewEditingVariantColor`, e.g. on the picker's `onCancel`. */
  clearPreviewEditingVariantColor: (
    variantId: string,
    key: ThemeColorKey
  ) => void;
  /** Same as `previewEditingVariantColor`, for one field of a highlight color. */
  previewEditingVariantHighlightColor: (
    variantId: string,
    highlightId: string,
    patch: Partial<ThemeHighlightColor>
  ) => void;
  /** Discards a pending `previewEditingVariantHighlightColor` field, e.g. on `onCancel`. */
  clearPreviewEditingVariantHighlightField: (
    variantId: string,
    highlightId: string,
    field: keyof ThemeHighlightColor
  ) => void;
  setEditingDefaultVariant: (variantId: string) => void;
  /** Removes a variant from the draft. No-op if it's the only remaining variant. */
  removeEditingVariant: (variantId: string) => void;
  /** Persists the viewer's variant choice for the currently active customization. Pass `SYSTEM_THEME_ID` to follow the device's color scheme instead of pinning one variant. No-op if none is active. */
  selectActiveVariant: (variantId: string) => Promise<void>;
  /** Sets an extension's availability on the draft, or with null, removes its entry so it follows the draft's default. No-op with no open draft. */
  setEditingExtensionAvailability: (
    extensionId: string,
    availability: ExtensionAvailability | null
  ) => void;
  /** Sets the availability the draft gives every extension with no explicit entry. No-op with no open draft. */
  setEditingDefaultExtensionAvailability: (
    availability: DefaultExtensionAvailability
  ) => void;
  /** The active customization's effective availability for an extension id. "available" when nothing is active; its default when the id has no explicit entry. */
  getActiveExtensionAvailability: (
    extensionId: string
  ) => ExtensionAvailability;
  /** Sets an extension setting's default value on the draft. No-op with no open draft. */
  setEditingExtensionSettingDefault: (
    extensionId: string,
    key: string,
    value: ExtensionSettingValue
  ) => void;
  /** Removes an extension setting's default value from the draft. No-op with no open draft. */
  clearEditingExtensionSettingDefault: (
    extensionId: string,
    key: string
  ) => void;
  /** The active customization's default for an extension setting, or undefined if nothing is active or it sets no default there. */
  /**
   * Records where one of the customization's own sensitive proxies is (or,
   * with null, forgets it) and saves the customization right away, since the
   * proxy record already exists. Updates the edit draft too when it's the
   * same customization. Resolves to whether it was saved.
   */
  setExtensionSensitiveProxy: (
    customizationId: string,
    extensionId: string,
    proxyId: string,
    pointer: SensitiveProxyPointer | null
  ) => Promise<boolean>;
  getActiveExtensionSettingDefault: (
    extensionId: string,
    key: string
  ) => ExtensionSettingValue | undefined;
  /** Installs an extension for the viewer on the active customization: an "available" one as an extra, an "auto-installed" one the viewer removed by putting it back. No-op if none is active or the extension is hidden there. */
  addExtensionToActiveCustomization: (extensionId: string) => Promise<void>;
  /** Uninstalls an extension for the viewer on the active customization: drops an extra, or records that they removed an "auto-installed" one. No-op if none is active. */
  removeExtensionFromActiveCustomization: (
    extensionId: string
  ) => Promise<void>;
}

/**
 * A prior SSR render's completed `?customization=...` load, handed to the
 * client so it can skip re-fetching over `os.getData()` — see
 * `CustomizationsManager.getInitialCustomizationSeed` (which produces this)
 * and `app/customizationSeed.ts` (which reads it back out of the injected
 * HTML on the client).
 */
export interface InitialCustomizationSeed {
  /**
   * The `?customization=...` locator this seed was resolved for. Checked
   * against the page's own `?customization=` param before use — a seed for a
   * different locator (a mismatched or stale cached HTML page, say) must
   * never be applied as if it were this page's own load.
   */
  locator: string;
  /**
   * The customization SSR resolved for `locator`, or null if that load
   * completed and found nothing (an invalid or deleted link). Only ever
   * built from a load that actually completed — see the caller in
   * `entry-ssr.tsx` — never from one the SSR-only timeout backstop cut
   * short, which would incorrectly tell the client "this doesn't exist" for
   * a link that might still resolve.
   *
   * Typed here as already-narrowed, but it crossed a server/client boundary
   * (embedded as JSON in the page) to get here — nothing enforces that shape
   * on the way in. The consumer (`createCustomizationsManager`'s
   * `parseSeedCustomization`) re-validates it through the same
   * `customizationSchema` a fresh fetch goes through before trusting it, so
   * treat this field as untrusted input, not as a guarantee.
   */
  customization: SeedBibleCustomization | null;
}

export function createCustomizationsManager(
  os: CasualOSManager,
  login: LoginManager,
  theme: ThemeManager,
  navigation: NavigationManager,
  variantSelections: CustomizationVariantSelectionsManager,
  extensionPreferences: CustomizationExtensionPreferencesManager,
  initialCustomizationSeed?: InitialCustomizationSeed,
  /** Apply the seed for the first paint, then download the record again anyway. */
  revalidateInitialCustomizationSeed = false
): CustomizationsManager {
  const customizations = signal<SeedBibleCustomization[]>([]);
  const isLoading = signal(false);
  const editingCustomization = signal<SeedBibleCustomization | null>(null);
  const editingVariantId = signal<string | null>(null);
  /**
   * In-memory-only color overrides from an open `ColorPicker`'s live drag on
   * a variant being edited, keyed by variant id. Never written into
   * `editingCustomization` or auto-saved — `resolveEditingVariantTheme`
   * layers these on top of the draft so a drag previews live (everywhere
   * the variant renders, including the whole app while it's also the
   * active one), and a cancel simply clears the entry.
   */
  const previewVariantOverrides = signal<Record<string, ThemeOverrides>>({});
  /** Same as `previewVariantOverrides`, for highlight colors. */
  const previewVariantHighlightOverrides = signal<
    Record<string, HighlightOverrides>
  >({});
  const linkedCustomization = signal<SeedBibleCustomization | null>(null);
  const linkedCustomizationLocator = signal<string | null>(null);

  const initialCustomizationLoadSettled = signal<boolean>(false);
  let resolveInitialCustomizationLoadPromise: () => void = () => {};
  const initialCustomizationLoadPromise = new Promise<void>((resolve) => {
    resolveInitialCustomizationLoadPromise = resolve;
  });

  const loadByLocator = async (locator: string): Promise<void> => {
    // `id` is always `customization_<uuid>` (no dots); split on the LAST dot
    // so a recordName that happens to contain one still parses correctly.
    const dotIndex = locator.lastIndexOf(".");
    if (dotIndex <= 0 || dotIndex === locator.length - 1) {
      console.warn("Invalid customization locator:", locator);
      return;
    }
    const recordName = locator.slice(0, dotIndex);
    const id = locator.slice(dotIndex + 1);

    try {
      const result = await os.getData(recordName, id);
      if (!result.success || !result.data) {
        console.warn("Customization not found for locator:", locator);
        return;
      }
      const parsed = customizationSchema.safeParse(result.data);
      if (!parsed.success) {
        console.warn(
          "Invalid customization record for locator:",
          locator,
          parsed.error
        );
        return;
      }
      linkedCustomization.value = {
        ...parsed.data,
        variants: narrowVariants(parsed.data.variants),
      };
      linkedCustomizationLocator.value = locator;
    } catch (error) {
      console.error(
        "Failed to load customization from locator:",
        locator,
        error
      );
    }
  };

  const initialLocator =
    navigation.initialUrl.searchParams.get("customization");

  let initialCustomizationLoadTimer: ReturnType<typeof setTimeout> | null =
    null;
  const settleInitialCustomizationLoad = () => {
    if (initialCustomizationLoadTimer !== null) {
      clearTimeout(initialCustomizationLoadTimer);
      initialCustomizationLoadTimer = null;
    }
    initialCustomizationLoadSettled.value = true;
    resolveInitialCustomizationLoadPromise();
  };

  /**
   * True once the initial `?customization=...` load has actually completed
   * (found, not found, or errored) — unlike `initialCustomizationLoadSettled`
   * above, this is never forced true by the SSR-only timeout backstop below.
   * `getInitialCustomizationSeed` relies on that distinction: seeding the
   * client with "not found" for a load the timeout merely gave up waiting on
   * would incorrectly rule out a customization that might still resolve.
   */
  let initialCustomizationLoadCompleted = false;

  /**
   * Validates a same-locator seed the same way `loadByLocator` validates a
   * freshly-fetched record — `initialCustomizationSeed.customization` is
   * typed as an already-narrowed `SeedBibleCustomization`, but it crossed a
   * server/client boundary (embedded as JSON in the page) to get here, so
   * nothing actually enforces that shape at this point except this parse.
   * Skipping it would let the fetch path and the seed path silently drift
   * apart the next time the schema changes (a new required field, a
   * different variant shape): the fetch path would reject a stale/bad
   * record, while the seed path would wave it through as-is.
   */
  function parseSeedCustomization(
    customization: SeedBibleCustomization
  ): SeedBibleCustomization | null {
    const parsed = customizationSchema.safeParse(customization);
    if (!parsed.success) {
      return null;
    }
    return {
      ...parsed.data,
      variants: narrowVariants(parsed.data.variants),
    };
  }

  if (initialLocator) {
    const seedForLocator =
      initialCustomizationSeed?.locator === initialLocator
        ? initialCustomizationSeed
        : null;

    // `customization: null` means the seed itself already resolved to "not
    // found" — nothing to validate, that outcome applies as-is. A non-null
    // seed still has to pass the same schema check `loadByLocator` applies to
    // a fresh fetch; an invalid one is treated as no seed at all, falling
    // through to a normal fetch below rather than trusting a shape the
    // schema no longer accepts.
    const validatedSeedCustomization =
      seedForLocator && seedForLocator.customization
        ? parseSeedCustomization(seedForLocator.customization)
        : null;
    const seedIsUsable =
      seedForLocator &&
      (seedForLocator.customization === null || validatedSeedCustomization);

    if (seedIsUsable) {
      // A prior SSR render already resolved this exact locator — apply its
      // result directly instead of repeating the `os.getData()` round trip.
      if (validatedSeedCustomization) {
        linkedCustomization.value = validatedSeedCustomization;
        linkedCustomizationLocator.value = initialLocator;
      }
      initialCustomizationLoadCompleted = true;
      settleInitialCustomizationLoad();
      if (revalidateInitialCustomizationSeed) {
        void loadByLocator(initialLocator);
      }
    } else {
      if (seedForLocator) {
        console.warn(
          "Ignoring an initialCustomizationSeed that failed validation; fetching instead:",
          initialLocator
        );
      }
      void loadByLocator(initialLocator).then(() => {
        initialCustomizationLoadCompleted = true;
        settleInitialCustomizationLoad();
      });

      // During SSR the render blocks on `initialCustomizationLoadPromise`, so
      // an `os.getData()` that never answers would hold the request open
      // indefinitely — `loadByLocator` itself always resolves (its try/catch
      // covers every other failure mode), so this timeout is purely a
      // backstop for that one case. Not armed on the client: the promise is
      // only thrown (to suspend) during SSR (see
      // `ExternalResourceDependencies` in `app/main.tsx`), so on the client
      // it's never awaited and a slow load simply applies the customization
      // late, exactly as it did before this feature existed.
      const SSR_INITIAL_CUSTOMIZATION_TIMEOUT_MS = 5000;
      if (import.meta.env.SSR) {
        initialCustomizationLoadTimer = setTimeout(() => {
          console.warn(
            "Timed out waiting for initial customization load:",
            initialLocator
          );
          settleInitialCustomizationLoad();
        }, SSR_INITIAL_CUSTOMIZATION_TIMEOUT_MS);
      }
    }
  } else {
    settleInitialCustomizationLoad();
  }

  /**
   * Builds a seed of the initial `?customization=...` load for
   * `entry-ssr.tsx` to embed in the page, so the client's own
   * `CustomizationsManager` can skip re-fetching what SSR already resolved —
   * see `InitialCustomizationSeed`. Null when there was no `?customization=`
   * param, or when the load hadn't actually completed by the time this was
   * called (the SSR-only timeout backstop fired first).
   */
  const getInitialCustomizationSeed = (): InitialCustomizationSeed | null => {
    if (!initialLocator || !initialCustomizationLoadCompleted) {
      return null;
    }
    return {
      locator: initialLocator,
      customization:
        linkedCustomizationLocator.value === initialLocator
          ? linkedCustomization.value
          : null,
    };
  };

  // The only two ways for a customization to become "active" (applied to
  // the live theme): an in-progress edit draft, or a `?customization=...`
  // share link. The draft wins when both are present — nothing is persisted
  // by opening an editor, so previewing your own edits should never be
  // blocked by whatever happened to be loaded from the URL.
  const activeCustomization = computed<SeedBibleCustomization | null>(
    () => editingCustomization.value ?? linkedCustomization.value
  );

  const activeCustomizationLocator = computed<string | null>(() => {
    const draft = editingCustomization.value;
    if (draft) {
      const recordName = login.userId.value;
      return recordName
        ? buildCustomizationLocator(recordName, draft.id)
        : null;
    }
    if (linkedCustomization.value) {
      return linkedCustomizationLocator.value;
    }
    return null;
  });

  // A super property rides on every later event (pageviews, chapter reads,
  // …), which is what lets analytics be split per customization; the person
  // property only remembers the latest one a signed-in viewer arrived through.
  // Session-scoped (sessionStorage, one tab) rather than `register`'s
  // persistent storage, which would leak the tag into a later plain visit's
  // events, or another tab's, until this effect got round to clearing it.
  // Keyed on the share link rather than `activeCustomizationLocator` so an
  // owner previewing their own draft isn't counted as a visit to it.
  effect(() => {
    const locator = linkedCustomizationLocator.value;
    const userId = login.userId.value;
    if (typeof posthog === "undefined" || !posthog) {
      return;
    }
    if (!locator) {
      posthog.unregister_for_session(POSTHOG_CUSTOMIZATION_PROPERTY);
      return;
    }
    posthog.register_for_session({ [POSTHOG_CUSTOMIZATION_PROPERTY]: locator });
    if (userId) {
      posthog.identify(userId, { [POSTHOG_CUSTOMIZATION_PROPERTY]: locator });
    }
  });

  const activeExtensionIds = computed<string[]>(() => {
    const customization = activeCustomization.value;
    if (!customization) {
      return [];
    }
    const locator = activeCustomizationLocator.value;
    const removed = locator
      ? extensionPreferences.getRemovedExtensionIds(locator)
      : [];
    const autoInstalled = Object.entries(customization.extensionSettings)
      .filter(([, availability]) => availability === "auto-installed")
      .map(([id]) => id)
      .filter((id) => !removed.includes(id));
    const extra = locator
      ? extensionPreferences.getExtraExtensionIds(locator)
      : [];
    // An extra id only applies while its availability is still "available" —
    // if the owner later marks it hidden or auto-installed, a stale extra
    // pick from before that change is ignored rather than force-applied.
    const validExtra = extra.filter(
      (id) => getExtensionAvailability(customization, id) === "available"
    );
    return Array.from(new Set([...autoInstalled, ...validExtra]));
  });

  const selectedVariantId = computed<string | null>(() => {
    const locator = activeCustomizationLocator.value;
    return locator ? variantSelections.getSelectedVariantId(locator) : null;
  });

  const canFollowSystemScheme = computed<boolean>(() => {
    const customization = activeCustomization.value;
    if (!customization) {
      return false;
    }
    return (
      customization.variants.some((v) => v.baseTheme === LIGHT_THEME.id) &&
      customization.variants.some((v) => v.baseTheme === DARK_THEME.id)
    );
  });

  const isFollowingSystemScheme = computed<boolean>(() => {
    if (!canFollowSystemScheme.value) {
      return false;
    }
    // An explicit pick wins, including the System card's own sentinel. Only
    // a viewer who has never picked a variant here inherits their app-wide
    // System preference.
    const selectedId = selectedVariantId.value;
    return selectedId
      ? selectedId === SYSTEM_THEME_ID
      : theme.selectedThemeId.value === SYSTEM_THEME_ID;
  });

  // A boolean, not the URL itself, so the active variant (and the theme built
  // from it) is recomputed only when embed mode flips, not on every navigation.
  const isMinimalEmbed = computed(() =>
    isMinimalEmbedUrl(navigation.currentUrl.value)
  );
  const activeVariant = computed<CustomizationThemeVariant | null>(() => {
    const customization = activeCustomization.value;
    if (!customization) {
      return null;
    }
    const byId = (id: string | null | undefined) =>
      id ? customization.variants.find((v) => v.id === id) : undefined;
    // The embed link names the theme. A saved variant pick would leave the
    // preview on one look no matter which theme the link asked for.
    if (isMinimalEmbed.value) {
      const themeId = theme.selectedThemeId.value;
      const baseThemeId =
        themeId === SYSTEM_THEME_ID
          ? theme.prefersDarkScheme.value
            ? DARK_THEME.id
            : LIGHT_THEME.id
          : themeId;
      const linked = customization.variants.find(
        (variant) => variant.baseTheme === baseThemeId
      );
      if (linked) {
        return linked;
      }
      // No variant matches the link's theme (only a light variant, or a
      // branding theme id). The viewer's saved pick is not what a visitor
      // to the embed sees — they get the customization's default.
      return (
        byId(customization.defaultVariantId) ??
        customization.variants[0] ??
        null
      );
    }
    const bySystemScheme = isFollowingSystemScheme.value
      ? customization.variants.find(
          (v) =>
            v.baseTheme ===
            (theme.prefersDarkScheme.value ? DARK_THEME.id : LIGHT_THEME.id)
        )
      : undefined;
    return (
      bySystemScheme ??
      byId(selectedVariantId.value) ??
      byId(customization.defaultVariantId) ??
      customization.variants[0] ??
      null
    );
  });

  const activeThemeOverrides = computed<ThemeOverrides>(
    () => activeVariant.value?.themes ?? {}
  );

  const activeHighlightOverrides = computed<HighlightOverrides>(
    () => activeVariant.value?.highlightColors ?? {}
  );

  const resolveVariantBaseTheme = (
    variant: CustomizationThemeVariant
  ): BibleTheme =>
    theme.themes.value.find((t) => t.id === variant.baseTheme) ??
    theme.basePresetTheme.value;

  /**
   * `variant`'s resolved theme, with any pending live-drag preview for it
   * layered on top — same merge style `buildBibleThemeFromCustomizationTheme`
   * itself uses (a plain `variables` spread, `applyHighlightOverrides` for
   * highlights), just sourced from `previewVariantOverrides` instead of the
   * variant's own persisted fields.
   */
  const resolveEditingVariantTheme = (
    variant: CustomizationThemeVariant
  ): BibleTheme => {
    const resolved = buildBibleThemeFromCustomizationTheme(
      variant,
      resolveVariantBaseTheme(variant)
    );
    const colorPreview = previewVariantOverrides.value[variant.id];
    const withColorPreview =
      !colorPreview || Object.keys(colorPreview).length === 0
        ? resolved
        : {
            ...resolved,
            variables: { ...resolved.variables, ...colorPreview },
          };
    const highlightPreview = previewVariantHighlightOverrides.value[variant.id];
    return highlightPreview
      ? applyHighlightOverrides(withColorPreview, highlightPreview)
      : withColorPreview;
  };

  const activeResolvedTheme = computed<BibleTheme | null>(() => {
    const variant = activeVariant.value;
    if (!variant) {
      return null;
    }
    return resolveEditingVariantTheme(variant);
  });

  /**
   * Whether the open draft (`editingCustomization`) differs from what's
   * actually persisted — i.e. an edit hasn't landed yet, whether because
   * the auto-save debounce is still pending or a write is in flight. Used
   * to decide whether to warn before discarding the draft. `false` when
   * there's no open draft.
   */
  const hasUnsavedChanges = computed<boolean>(() => {
    const draft = editingCustomization.value;
    if (!draft) {
      return false;
    }
    const persisted = customizations.value.find((c) => c.id === draft.id);
    if (!persisted) {
      return true;
    }
    return JSON.stringify(draft) !== JSON.stringify(persisted);
  });

  const load = async () => {
    const userId = login.userId.value;
    if (!userId) {
      customizations.value = [];
      return;
    }

    isLoading.value = true;
    try {
      const result = await os.listAllDataByMarker(userId, CUSTOMIZATION_MARKER);
      const loaded: SeedBibleCustomization[] = [];
      for (const item of result.items) {
        const parsed = customizationSchema.safeParse(item.data);
        if (!parsed.success) {
          console.warn("Skipping invalid customization record:", parsed.error);
          continue;
        }
        loaded.push({
          ...parsed.data,
          variants: narrowVariants(parsed.data.variants),
        });
      }
      customizations.value = loaded;
    } catch (error) {
      console.error("Failed to load customizations:", error);
    } finally {
      isLoading.value = false;
    }
  };

  const persist = (userId: string, record: SeedBibleCustomization) =>
    os.recordData(userId, record.id, record, {
      marker: CUSTOMIZATION_MARKER,
    });

  /**
   * Keeps a share-link copy of the owner's own customization in step with a
   * save. Without this, closing the editor falls back to the copy fetched
   * when the link loaded, so the owner sees their edits undone until a reload.
   */
  const updateLinkedCopy = (
    userId: string,
    saved: SeedBibleCustomization
  ): void => {
    if (
      linkedCustomizationLocator.value ===
      buildCustomizationLocator(userId, saved.id)
    ) {
      linkedCustomization.value = saved;
    }
  };

  // Every draft save and pointer write goes through here, one at a time, so
  // they reach the server in the order they were made: an older copy can't
  // land last and overwrite a newer one, which for a pointer would leave its
  // proxy (and the owner's key) running with nothing pointing at it.
  let writeChain: Promise<unknown> = Promise.resolve();
  const queueWrite = <T,>(run: () => Promise<T>): Promise<T> => {
    const next = writeChain.then(run, run);
    writeChain = next.catch(() => undefined);
    return next;
  };

  const create = async (): Promise<SeedBibleCustomization> => {
    const userId = login.userId.value;
    if (!userId) {
      throw new Error("Cannot create a customization while signed out.");
    }

    const now = Date.now();
    const preset = theme.basePresetTheme.value;
    const variant = buildVariant(preset.name, preset.id);
    const record: SeedBibleCustomization = {
      id: `customization_${uuid()}`,
      name: `Custom Branding ${customizations.value.length + 1}`,
      variants: [variant],
      defaultVariantId: variant.id,
      logoUrl: null,
      createdAt: now,
      updatedAt: now,
      extensionSettings: {},
      extensionSettingDefaults: {},
      extensionSensitiveProxies: {},
    };

    await persist(userId, record);
    customizations.value = [...customizations.value, record];
    return record;
  };

  const startEditing = (id: string): void => {
    const existing = customizations.value.find((c) => c.id === id);
    if (!existing) {
      return;
    }
    editingCustomization.value = existing;
  };

  /**
   * Clears `editingCustomization` and `editingVariantId`. Any edit still
   * waiting on the auto-save debounce is flushed first (in the background —
   * this stays synchronous, since callers rely on the draft being gone
   * immediately), so closing the editor right after a change doesn't drop it.
   */
  const stopEditing = (): void => {
    if (autoSaveTimer !== null) {
      void flushAutoSave();
    }
    editingCustomization.value = null;
    editingVariantId.value = null;
  };

  /**
   * Clears `editingCustomization` and `editingVariantId` the same as
   * `stopEditing`, except any edit still waiting on the auto-save debounce
   * is cancelled instead of flushed — the draft's unsaved changes are
   * genuinely thrown away rather than written on the way out. An edit
   * whose auto-save has already started (the 5-second debounce already
   * fired) can't be un-sent and will still land.
   */
  const discardEditingCustomization = (): void => {
    if (autoSaveTimer !== null) {
      clearTimeout(autoSaveTimer);
      autoSaveTimer = null;
    }
    editingCustomization.value = null;
    editingVariantId.value = null;
  };

  const saveEditingCustomization = async (): Promise<void> => {
    if (autoSaveTimer !== null) {
      clearTimeout(autoSaveTimer);
      autoSaveTimer = null;
    }
    const userId = login.userId.value;
    const current = editingCustomization.value;
    if (!userId || !current) {
      return;
    }

    const saved: SeedBibleCustomization = { ...current, updatedAt: Date.now() };
    await queueWrite(async () => {
      // Nothing local changes unless the server kept the record, so a failed
      // save keeps the draft unsaved instead of looking applied.
      const result = await persist(userId, saved);
      if (!result.success) {
        throw new Error(`Failed to save customization: ${result.errorCode}`);
      }
      customizations.value = customizations.value.some((c) => c.id === saved.id)
        ? customizations.value.map((c) => (c.id === saved.id ? saved : c))
        : [...customizations.value, saved];
      updateLinkedCopy(userId, saved);
    });
    // Only reflect the write onto the draft if it's still the exact one
    // captured above — a newer edit, or the editor closing, may have
    // changed `editingCustomization` while this (possibly auto-triggered)
    // save was in flight, and must not be clobbered by this now-stale copy.
    if (editingCustomization.value === current) {
      editingCustomization.value = saved;
    }
  };

  /** How long to wait after the last edit before auto-saving the draft. */
  const AUTO_SAVE_DEBOUNCE_MS = 5000;
  let autoSaveTimer: ReturnType<typeof setTimeout> | null = null;

  /** Queues a debounced save of the current draft. */
  const scheduleAutoSave = (): void => {
    if (autoSaveTimer !== null) {
      clearTimeout(autoSaveTimer);
    }
    autoSaveTimer = setTimeout(() => {
      autoSaveTimer = null;
      void flushAutoSave();
    }, AUTO_SAVE_DEBOUNCE_MS);
  };

  /**
   * Writes the current draft immediately. Resolves once it has landed.
   * Starts the write synchronously (rather than deferring it into a
   * `.then()`) so it captures `editingCustomization.value` as it is right
   * now — callers like `stopEditing()` clear that signal on the very next
   * line, before this promise has a chance to resolve.
   */
  const flushAutoSave = (): Promise<void> =>
    saveEditingCustomization().catch((error) => {
      console.error("Failed to auto-save customization:", error);
    });

  const updateEditingName = (name: string): void => {
    const current = editingCustomization.value;
    if (!current) {
      return;
    }
    editingCustomization.value = { ...current, name, updatedAt: Date.now() };
    scheduleAutoSave();
  };

  const updateEditingDefaultTranslationId = (
    translationId: string | null
  ): void => {
    const current = editingCustomization.value;
    if (!current) {
      return;
    }
    editingCustomization.value = {
      ...current,
      defaultTranslationId: translationId ?? undefined,
      updatedAt: Date.now(),
    };
    scheduleAutoSave();
  };

  const addEditingVariant = (): CustomizationThemeVariant | null => {
    const current = editingCustomization.value;
    if (!current) {
      return null;
    }

    const preset = theme.basePresetTheme.value;
    const baseName = preset.name;
    const usedNames = new Set(current.variants.map((v) => v.name));
    const name = usedNames.has(baseName)
      ? `Variant ${current.variants.length + 1}`
      : baseName;
    const variant = buildVariant(name, preset.id);

    editingCustomization.value = {
      ...current,
      variants: [...current.variants, variant],
      updatedAt: Date.now(),
    };
    scheduleAutoSave();
    return variant;
  };

  /**
   * Changes which built-in preset one variant falls back to for anything it
   * hasn't explicitly overridden — "base" this theme on Light or Dark so it
   * starts from a known-good reference instead of hand-tweaking every
   * field. Every field the user has already customized (present in
   * `themes`/`highlightColors`) is left completely untouched; only the
   * still-inherited fields will visually change. No-ops if there's no open
   * draft, the variant id doesn't match, or `presetId` is unrecognized.
   */
  const applyPresetToEditingVariant = (
    variantId: string,
    presetId: string
  ): void => {
    const current = editingCustomization.value;
    if (!current) {
      return;
    }
    const preset = theme.themes.value.find((t) => t.id === presetId);
    if (!preset) {
      return;
    }

    editingCustomization.value = {
      ...current,
      variants: current.variants.map((v) =>
        v.id === variantId
          ? { ...v, baseTheme: preset.id, updatedAt: Date.now() }
          : v
      ),
      updatedAt: Date.now(),
    };
    scheduleAutoSave();
  };

  const renameEditingVariant = (variantId: string, name: string): void => {
    const current = editingCustomization.value;
    if (!current) {
      return;
    }
    editingCustomization.value = {
      ...current,
      variants: current.variants.map((v) =>
        v.id === variantId ? { ...v, name, updatedAt: Date.now() } : v
      ),
      updatedAt: Date.now(),
    };
    scheduleAutoSave();
  };

  const previewEditingVariantColor = (
    variantId: string,
    key: ThemeColorKey,
    value: string
  ): void => {
    previewVariantOverrides.value = {
      ...previewVariantOverrides.value,
      [variantId]: {
        ...previewVariantOverrides.value[variantId],
        [key]: value,
      },
    };
  };

  const clearPreviewEditingVariantColor = (
    variantId: string,
    key: ThemeColorKey
  ): void => {
    const existing = previewVariantOverrides.value[variantId];
    if (!existing || !(key in existing)) return;
    const next = { ...existing };
    delete next[key];
    const nextAll = { ...previewVariantOverrides.value };
    if (Object.keys(next).length === 0) {
      delete nextAll[variantId];
    } else {
      nextAll[variantId] = next;
    }
    previewVariantOverrides.value = nextAll;
  };

  const setEditingVariantColor = (
    variantId: string,
    key: ThemeColorKey,
    value: string
  ): void => {
    const current = editingCustomization.value;
    if (!current) {
      return;
    }
    batch(() => {
      editingCustomization.value = {
        ...current,
        variants: current.variants.map((variant) => {
          if (variant.id !== variantId) {
            return variant;
          }
          if (key !== "primaryColor") {
            return {
              ...variant,
              themes: { ...variant.themes, [key]: value },
              updatedAt: Date.now(),
            };
          }

          // Secondary/tertiary follow the primary color as long as they still
          // match its lightened derivation — including "never touched at
          // all" (inherited from the base preset), which counts as following
          // too. The moment a user manually picks one, it stops matching and
          // is left alone on future primary edits.
          const previousPrimary =
            variant.themes.primaryColor ??
            resolveVariantBaseTheme(variant).variables.primaryColor;
          const nextThemes: ThemeOverrides = {
            ...variant.themes,
            primaryColor: value,
          };
          if (
            variant.themes.secondaryColor === undefined ||
            variant.themes.secondaryColor ===
              lightenColor(previousPrimary, SECONDARY_LIGHTEN_AMOUNT)
          ) {
            nextThemes.secondaryColor = lightenColor(
              value,
              SECONDARY_LIGHTEN_AMOUNT
            );
          }
          if (
            variant.themes.tertiaryColor === undefined ||
            variant.themes.tertiaryColor ===
              lightenColor(previousPrimary, TERTIARY_LIGHTEN_AMOUNT)
          ) {
            nextThemes.tertiaryColor = lightenColor(
              value,
              TERTIARY_LIGHTEN_AMOUNT
            );
          }

          return { ...variant, themes: nextThemes, updatedAt: Date.now() };
        }),
        updatedAt: Date.now(),
      };
      clearPreviewEditingVariantColor(variantId, key);
      scheduleAutoSave();
    });
  };

  const setEditingVariantFont = (
    variantId: string,
    key: ThemeFontFamilyKey,
    value: string
  ): void => {
    const current = editingCustomization.value;
    if (!current) {
      return;
    }
    editingCustomization.value = {
      ...current,
      variants: current.variants.map((variant) =>
        variant.id === variantId
          ? {
              ...variant,
              themes: { ...variant.themes, [key]: value },
              updatedAt: Date.now(),
            }
          : variant
      ),
      updatedAt: Date.now(),
    };
    scheduleAutoSave();
  };

  const previewEditingVariantHighlightColor = (
    variantId: string,
    highlightId: string,
    patch: Partial<ThemeHighlightColor>
  ): void => {
    const existingForVariant =
      previewVariantHighlightOverrides.value[variantId] ?? {};
    const existingForId = existingForVariant[highlightId] ?? {};
    previewVariantHighlightOverrides.value = {
      ...previewVariantHighlightOverrides.value,
      [variantId]: {
        ...existingForVariant,
        [highlightId]: { ...existingForId, ...patch },
      },
    };
  };

  const clearPreviewEditingVariantHighlightField = (
    variantId: string,
    highlightId: string,
    field: keyof ThemeHighlightColor
  ): void => {
    const existingForVariant =
      previewVariantHighlightOverrides.value[variantId];
    const existingForId = existingForVariant?.[highlightId];
    if (!existingForId || !(field in existingForId)) return;
    const nextForId = { ...existingForId };
    delete nextForId[field];
    const nextForVariant = { ...existingForVariant };
    if (Object.keys(nextForId).length === 0) {
      delete nextForVariant[highlightId];
    } else {
      nextForVariant[highlightId] = nextForId;
    }
    const nextAll = { ...previewVariantHighlightOverrides.value };
    if (Object.keys(nextForVariant).length === 0) {
      delete nextAll[variantId];
    } else {
      nextAll[variantId] = nextForVariant;
    }
    previewVariantHighlightOverrides.value = nextAll;
  };

  const setEditingVariantHighlightColor = (
    variantId: string,
    highlightId: string,
    patch: Partial<ThemeHighlightColor>
  ): void => {
    const current = editingCustomization.value;
    if (!current) {
      return;
    }
    batch(() => {
      editingCustomization.value = {
        ...current,
        variants: current.variants.map((variant) => {
          if (variant.id !== variantId) {
            return variant;
          }
          const existing = variant.highlightColors[highlightId] ?? {};
          return {
            ...variant,
            highlightColors: {
              ...variant.highlightColors,
              [highlightId]: { ...existing, ...patch },
            },
            updatedAt: Date.now(),
          };
        }),
        updatedAt: Date.now(),
      };
      for (const field of Object.keys(patch) as (keyof ThemeHighlightColor)[]) {
        clearPreviewEditingVariantHighlightField(variantId, highlightId, field);
      }
      scheduleAutoSave();
    });
  };

  /** Removes one field's override, reverting it to inherit from the variant's `baseTheme`. No-op with no open draft. */
  const resetEditingVariantField = (
    variantId: string,
    key: ThemeColorKey | ThemeFontFamilyKey
  ): void => {
    const current = editingCustomization.value;
    if (!current) {
      return;
    }
    editingCustomization.value = {
      ...current,
      variants: current.variants.map((variant) => {
        if (variant.id !== variantId) {
          return variant;
        }
        const nextThemes = { ...variant.themes };
        delete nextThemes[key];
        return { ...variant, themes: nextThemes, updatedAt: Date.now() };
      }),
      updatedAt: Date.now(),
    };
    scheduleAutoSave();
  };

  /** Removes all of one highlight id's overrides, reverting it to inherit from the variant's `baseTheme`. No-op with no open draft. */
  const resetEditingVariantHighlightColor = (
    variantId: string,
    highlightId: string
  ): void => {
    const current = editingCustomization.value;
    if (!current) {
      return;
    }
    editingCustomization.value = {
      ...current,
      variants: current.variants.map((variant) => {
        if (variant.id !== variantId) {
          return variant;
        }
        const nextHighlightColors = { ...variant.highlightColors };
        delete nextHighlightColors[highlightId];
        return {
          ...variant,
          highlightColors: nextHighlightColors,
          updatedAt: Date.now(),
        };
      }),
      updatedAt: Date.now(),
    };
    scheduleAutoSave();
  };

  const setEditingDefaultVariant = (variantId: string): void => {
    const current = editingCustomization.value;
    if (!current || !current.variants.some((v) => v.id === variantId)) {
      return;
    }
    editingCustomization.value = {
      ...current,
      defaultVariantId: variantId,
      updatedAt: Date.now(),
    };
    scheduleAutoSave();
  };

  const removeEditingVariant = (variantId: string): void => {
    const current = editingCustomization.value;
    if (!current || current.variants.length <= 1) {
      return;
    }
    if (!current.variants.some((v) => v.id === variantId)) {
      return;
    }

    const remaining = current.variants.filter((v) => v.id !== variantId);
    const nextDefaultVariantId =
      current.defaultVariantId === variantId
        ? remaining[0]!.id
        : current.defaultVariantId;

    editingCustomization.value = {
      ...current,
      variants: remaining,
      defaultVariantId: nextDefaultVariantId,
      updatedAt: Date.now(),
    };
    scheduleAutoSave();
  };

  const remove = async (id: string): Promise<void> => {
    const userId = login.userId.value;
    const existing = customizations.value.find((c) => c.id === id);
    if (!userId || !existing) {
      return;
    }

    // The proxies go first: once the record is gone nothing points at them,
    // and a public one would stay usable by anyone who saw its address.
    for (const byProxy of Object.values(existing.extensionSensitiveProxies)) {
      for (const pointer of Object.values(byProxy)) {
        const erased = await os.eraseProxy(pointer.recordName, pointer.address);
        if (!erased.success && erased.errorCode !== "data_not_found") {
          throw new Error(
            `Failed to clear a customization's sensitive settings: ${erased.errorCode}`
          );
        }
      }
    }

    const result = await os.eraseData(userId, id);
    if (!result.success && result.errorCode !== "data_not_found") {
      throw new Error(`Failed to delete customization: ${result.errorCode}`);
    }
    customizations.value = customizations.value.filter((c) => c.id !== id);
    if (editingCustomization.value?.id === id) {
      editingCustomization.value = null;
    }
  };

  const uploadLogo = async (file: File): Promise<void> => {
    const userId = login.userId.value;
    if (!userId) {
      throw new Error("Cannot upload a logo while signed out.");
    }
    if (!editingCustomization.value) {
      return;
    }

    const result = await os.recordFile(userId, file, {
      mimeType: file.type,
      marker: CUSTOMIZATION_MARKER,
    });
    if (result.success === false) {
      throw new Error("Failed to upload logo.");
    }

    // Re-read after the await: editing may have been cancelled while the
    // upload was in flight. Only stage the URL onto the draft — the record
    // itself isn't persisted until saveEditingCustomization() is called.
    const current = editingCustomization.value;
    if (!current) {
      return;
    }
    editingCustomization.value = {
      ...current,
      logoUrl: result.url,
      updatedAt: Date.now(),
    };
    await saveEditingCustomization();
  };

  const removeEditingLogo = async (): Promise<void> => {
    const current = editingCustomization.value;
    if (!current) {
      return;
    }
    editingCustomization.value = {
      ...current,
      logoUrl: null,
      updatedAt: Date.now(),
    };
    await saveEditingCustomization();
  };

  const setEditingExtensionAvailability = (
    extensionId: string,
    availability: ExtensionAvailability | null
  ): void => {
    const current = editingCustomization.value;
    if (!current) {
      return;
    }
    // An explicit entry is kept even when it matches the current default, so
    // that the author's choice for this extension survives a later change
    // of the default.
    const nextSettings = { ...current.extensionSettings };
    if (availability === null) {
      delete nextSettings[extensionId];
    } else {
      nextSettings[extensionId] = availability;
    }
    editingCustomization.value = {
      ...current,
      extensionSettings: nextSettings,
      updatedAt: Date.now(),
    };
    scheduleAutoSave();
  };

  const setEditingDefaultExtensionAvailability = (
    availability: DefaultExtensionAvailability
  ): void => {
    const current = editingCustomization.value;
    if (!current) {
      return;
    }
    editingCustomization.value = {
      ...current,
      defaultExtensionAvailability: availability,
      updatedAt: Date.now(),
    };
    scheduleAutoSave();
  };

  const getActiveExtensionAvailability = (
    extensionId: string
  ): ExtensionAvailability =>
    getExtensionAvailability(activeCustomization.value, extensionId);

  const setEditingExtensionSettingDefault = (
    extensionId: string,
    key: string,
    value: ExtensionSettingValue
  ): void => {
    const current = editingCustomization.value;
    if (!current) {
      return;
    }
    editingCustomization.value = {
      ...current,
      extensionSettingDefaults: {
        ...current.extensionSettingDefaults,
        [extensionId]: {
          ...current.extensionSettingDefaults[extensionId],
          [key]: value,
        },
      },
      updatedAt: Date.now(),
    };
    scheduleAutoSave();
  };

  const clearEditingExtensionSettingDefault = (
    extensionId: string,
    key: string
  ): void => {
    const current = editingCustomization.value;
    const currentExtensionDefaults =
      current?.extensionSettingDefaults[extensionId];
    if (!current || !currentExtensionDefaults) {
      return;
    }
    const nextExtensionDefaults = { ...currentExtensionDefaults };
    delete nextExtensionDefaults[key];
    editingCustomization.value = {
      ...current,
      extensionSettingDefaults: {
        ...current.extensionSettingDefaults,
        [extensionId]: nextExtensionDefaults,
      },
      updatedAt: Date.now(),
    };
    scheduleAutoSave();
  };

  const getActiveExtensionSettingDefault = (
    extensionId: string,
    key: string
  ): ExtensionSettingValue | undefined =>
    getExtensionSettingDefault(activeCustomization.value, extensionId, key);

  const setExtensionSensitiveProxy = async (
    customizationId: string,
    extensionId: string,
    proxyId: string,
    pointer: SensitiveProxyPointer | null
  ): Promise<boolean> => {
    const userId = login.userId.value;
    if (
      !userId ||
      !customizations.value.some((c) => c.id === customizationId)
    ) {
      return false;
    }
    // The draft gets the pointer now, so any later save of it carries the
    // pointer instead of dropping it. The write itself starts from the last
    // saved copy instead of the draft: the draft may hold edits the author
    // hasn't saved yet, and might still discard.
    // The draft and the saved copy share one timestamp, so once the write
    // lands they compare equal and the draft doesn't read as unsaved.
    const updatedAt = Date.now();
    const draft = editingCustomization.value;
    const previous =
      draft?.id === customizationId
        ? draft.extensionSensitiveProxies[extensionId]?.[proxyId]
        : undefined;
    if (draft?.id === customizationId) {
      editingCustomization.value = withExtensionSensitiveProxy(
        draft,
        extensionId,
        proxyId,
        pointer,
        updatedAt
      );
    }
    // A failed write takes the pointer back off the draft, unless something
    // newer has replaced it there since.
    const undoDraft = () => {
      const current = editingCustomization.value;
      if (
        current?.id === customizationId &&
        (current.extensionSensitiveProxies[extensionId]?.[proxyId] ?? null) ===
          pointer
      ) {
        editingCustomization.value = withExtensionSensitiveProxy(
          current,
          extensionId,
          proxyId,
          previous ?? null,
          Date.now()
        );
      }
    };
    const written = await queueWrite(async () => {
      const saved = customizations.value.find((c) => c.id === customizationId);
      if (!saved || login.userId.value !== userId) {
        return false;
      }
      const next = withExtensionSensitiveProxy(
        saved,
        extensionId,
        proxyId,
        pointer,
        updatedAt
      );
      try {
        const result = await persist(userId, next);
        if (!result.success) {
          console.error(
            "Failed to save a customization's sensitive settings:",
            result.errorCode
          );
          return false;
        }
      } catch (error) {
        console.error(
          "Failed to save a customization's sensitive settings:",
          error
        );
        return false;
      }
      customizations.value = customizations.value.map((c) =>
        c.id === customizationId ? next : c
      );
      updateLinkedCopy(userId, next);
      return true;
    });
    if (!written) {
      undoDraft();
    }
    return written;
  };

  const addExtensionToActiveCustomization = async (
    extensionId: string
  ): Promise<void> => {
    const locator = activeCustomizationLocator.value;
    if (!locator) {
      return;
    }
    const availability = getActiveExtensionAvailability(extensionId);
    if (availability === "hidden") {
      return;
    }
    if (availability === "auto-installed") {
      await extensionPreferences.clearRemovedExtensionId(locator, extensionId);
      return;
    }
    await extensionPreferences.addExtraExtensionId(locator, extensionId);
  };

  const removeExtensionFromActiveCustomization = async (
    extensionId: string
  ): Promise<void> => {
    const locator = activeCustomizationLocator.value;
    if (!locator) {
      return;
    }
    if (getActiveExtensionAvailability(extensionId) === "auto-installed") {
      await extensionPreferences.addRemovedExtensionId(locator, extensionId);
      return;
    }
    await extensionPreferences.removeExtraExtensionId(locator, extensionId);
  };

  const getShareLink = (customization: SeedBibleCustomization): string => {
    const recordName = login.userId.value ?? "";
    return navigation.linkToBareRoot({
      customization: buildCustomizationLocator(recordName, customization.id),
    });
  };

  const selectActiveVariant = async (variantId: string): Promise<void> => {
    const locator = activeCustomizationLocator.value;
    if (!locator) {
      return;
    }
    await variantSelections.selectVariant(locator, variantId);
  };

  return {
    customizations,
    isLoading,
    activeCustomization,
    activeCustomizationLocator,
    activeVariant,
    canFollowSystemScheme,
    isFollowingSystemScheme,
    activeThemeOverrides,
    activeHighlightOverrides,
    activeResolvedTheme,
    resolveVariantBaseTheme,
    resolveEditingVariantTheme,
    activeExtensionIds,
    linkedCustomization,
    initialCustomizationLoadPromise,
    initialCustomizationLoadSettled,
    getInitialCustomizationSeed,
    editingCustomization,
    editingVariantId,
    load,
    loadByLocator,
    create,
    startEditing,
    stopEditing,
    discardEditingCustomization,
    hasUnsavedChanges,
    saveEditingCustomization,
    remove,
    uploadLogo,
    getShareLink,
    updateEditingName,
    updateEditingDefaultTranslationId,
    removeEditingLogo,
    addEditingVariant,
    applyPresetToEditingVariant,
    renameEditingVariant,
    setEditingVariantColor,
    setEditingVariantFont,
    setEditingVariantHighlightColor,
    resetEditingVariantField,
    resetEditingVariantHighlightColor,
    previewEditingVariantColor,
    clearPreviewEditingVariantColor,
    previewEditingVariantHighlightColor,
    clearPreviewEditingVariantHighlightField,
    setEditingDefaultVariant,
    removeEditingVariant,
    selectActiveVariant,
    setEditingExtensionAvailability,
    setEditingDefaultExtensionAvailability,
    getActiveExtensionAvailability,
    setEditingExtensionSettingDefault,
    clearEditingExtensionSettingDefault,
    getActiveExtensionSettingDefault,
    setExtensionSensitiveProxy,
    addExtensionToActiveCustomization,
    removeExtensionFromActiveCustomization,
  };
}
